/**
 * `/sym/etf-components/*` and `/sym/etf-holders/*` — what an ETF holds, and
 * which ETFs hold a stock (`docs/plans/etf-components.md`).
 *
 * Both are fetched when a tab opens and cached by the server for the market
 * day; nothing here caches. Reads degrade: `undefined` is "no answer" —
 * unconfigured, offline, or the vendor down with nothing cached (502).
 */
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

export interface EtfLine {
  /** Null for a line that is not a ticker: cash, a money-market fund, a future. */
  symbol: string | null;
  name: string | null;
  /** Percent of the fund, as published (8.2 is 8.2%). */
  weight: number | null;
  sector: string | null;
  /** Whether we chart it — a line that is not stays in the list, greyed. */
  inUniverse: boolean;
  close: number | null;
  /** The day's move from the previous close. */
  changePercentCc: number | null;
}

export interface EtfComponents {
  etf: string;
  /** The basket's own date, from the fund. */
  asOf: string | null;
  fetchedOn: string;
  lines: EtfLine[];
}

export interface EtfHolder {
  etf: string;
  name: string | null;
  weight: number | null;
  /** $ held; null when the source sent none. */
  value: number | null;
}

export interface LeveragedHolder {
  etf: string;
  name: string | null;
  /** From the fund's name; null when the name says leveraged without a number. */
  multiple: number | null;
  direction: "long" | "short";
  value: number | null;
}

export interface EtfHolders {
  symbol: string;
  /** The source carries no date, so this is the day it was fetched. */
  fetchedOn: string;
  /** Funds worldwide, before cleaning. */
  raw: number;
  funds: EtfHolder[];
  leveraged: LeveragedHolder[];
}

async function getJson(path: string): Promise<unknown> {
  if (!BASE) return undefined;
  try {
    const res = await fetch(`${BASE}${path}`, { headers: await requestHeaders() });
    return res.ok ? await res.json() : undefined;
  } catch {
    return undefined;
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const opt = (v: any): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: any): string | null => (typeof v === "string" && v ? v : null);

export function mapComponents(j: any): EtfComponents {
  return {
    etf: String(j.etf ?? ""),
    asOf: str(j.asOf),
    fetchedOn: String(j.fetchedOn ?? ""),
    lines: (j.lines ?? []).map((l: any): EtfLine => ({
      symbol: str(l.symbol),
      name: str(l.name),
      weight: opt(l.weight),
      sector: str(l.sector),
      inUniverse: l.inUniverse === true,
      close: opt(l.close),
      changePercentCc: opt(l.changePercentCc),
    })),
  };
}

export function mapHolders(j: any): EtfHolders {
  return {
    symbol: String(j.symbol ?? ""),
    fetchedOn: String(j.fetchedOn ?? ""),
    raw: opt(j.raw) ?? 0,
    funds: (j.funds ?? []).map((f: any): EtfHolder => ({
      etf: String(f.etf), name: str(f.name), weight: opt(f.weight), value: opt(f.value),
    })),
    leveraged: (j.leveraged ?? []).map((f: any): LeveragedHolder => ({
      etf: String(f.etf), name: str(f.name), multiple: opt(f.multiple),
      direction: f.direction === "short" ? "short" : "long", value: opt(f.value),
    })),
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export async function fetchEtfComponents(etf: string): Promise<EtfComponents | undefined> {
  if (!etf) return undefined;
  const j = await getJson(`/sym/etf-components/${encodeURIComponent(etf)}`);
  return j ? mapComponents(j) : undefined;
}

export async function fetchEtfHolders(symbol: string): Promise<EtfHolders | undefined> {
  if (!symbol) return undefined;
  const j = await getJson(`/sym/etf-holders/${encodeURIComponent(symbol)}`);
  return j ? mapHolders(j) : undefined;
}
